import { formatPace } from '@painpal/core';
import type { ThresholdInput } from '@painpal/contracts';
import { useLayoutEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { editPace, parseThresholdInput, type ThresholdCopy } from './thresholdModel';
import styles from './settings.module.css';

export interface ThresholdFormProps {
  copy: ThresholdCopy;
  initial: ThresholdInput;
  today: string;
  busy: boolean;
  error: string | null;
  onSubmit: (input: ThresholdInput) => void;
  onCancel: () => void;
}

export function ThresholdForm({ copy, initial, today, busy, error, onSubmit, onCancel }: ThresholdFormProps) {
  const pace = copy.unit === '/KM';
  const [value, setValue] = useState(pace ? formatPace(initial.value) : String(initial.value));
  const [date, setDate] = useState(initial.effectiveFrom);
  const [invalid, setInvalid] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current === null) return;
    input.current?.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [value]);
  function change(event: ChangeEvent<HTMLInputElement>): void {
    const { value: next, selectionStart } = event.currentTarget;
    if (!pace) return setValue(next);
    // A text input always has a selection, so selectionStart is never null here.
    const edit = editPace(value, next, Number(selectionStart));
    caret.current = edit.caret;
    setValue(edit.value);
  }
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const parsed = parseThresholdInput(copy, value, date);
    if (typeof parsed === 'string') return setInvalid(parsed);
    setInvalid(null);
    onSubmit(parsed);
  }
  const message = invalid ?? error;
  return (
    <form className={styles.thresholdForm} onSubmit={submit} aria-label={`${copy.short} entry`}>
      <label className={styles.formField}>
        <span>{pace ? `${copy.short} (MIN/KM)` : `${copy.short} (${copy.unit})`}</span>
        <input ref={input} type={pace ? 'text' : 'number'} inputMode="numeric" value={value} onChange={change} />
      </label>
      <label className={styles.formField}>
        <span>VALID FROM</span>
        <input type="date" value={date} max={today} onChange={(e) => setDate(e.currentTarget.value)} />
      </label>
      <button type="submit" className="btn btn-ink" disabled={busy}>SAVE</button>
      <button type="button" className="btn btn-outline" onClick={onCancel}>CANCEL</button>
      {message !== null && <p role="alert" className={`alert-hot ${styles.formError}`}>{message}</p>}
    </form>
  );
}
